import { Navigate, type RouteObject } from 'react-router';
import { RequireAuth } from '@/app/auth/RequireAuth';
import { RouteErrorBoundary } from '@/features/shared/RouteErrorBoundary';
import type { PortalArea } from '@/api/portal';

/*
 * The customer portal's route tree (ADR-0039 §6): mounted at `/portal` OUTSIDE `AppLayout` — its own shell —
 * and inside `RequireAuth`, entered with `portal:portal:access` (or `threepl:portal:access` while roles
 * migrate), or for an internal user's read-only preview of one customer (P-13). Every area answers to the tenant's effective configuration through `PortalAreaGuard`; static
 * words (`new`, `request`) rank above `:id`.
 */

const lazy = (load: () => Promise<Record<string, unknown>>, name: string) => () => load().then((m) => ({ Component: m[name] as React.ComponentType }));

/*
 * C7: the portal's layout and area guards read the portal configuration API, so they load with the portal
 * rather than with the internal shell's entry chunk.
 */
const guard = (area: PortalArea | PortalArea[]) => () => import('./components/PortalAreaGuard').then((m) => ({ element: <m.PortalAreaGuard area={area} /> }));

/*
 * P-13 (E5): every route that writes sits behind the preview's read-only notice, so a preview never opens a form the
 * server would refuse anyway (403 `portal.preview_read_only`).
 */
const readOnly = () => import('./preview').then((m) => ({ element: <m.PreviewReadOnlyGuard /> }));
const writes = (...routes: RouteObject[]): RouteObject => ({ lazy: readOnly, children: routes });

export const portalChildRoutes: RouteObject[] = [
  { index: true, lazy: lazy(() => import('./pages/OverviewPage'), 'OverviewPage') },
  {
    path: 'shipments',
    lazy: guard('SHIPMENTS'),
    children: [
      { index: true, lazy: lazy(() => import('./pages/ShipmentsPage'), 'ShipmentsPage') },
      { path: ':id', lazy: lazy(() => import('./pages/DetailPages'), 'ShipmentPage') },
    ],
  },
  {
    // L-10c (D-325): the customer books a transport; the list is the bookings not yet under way.
    path: 'bookings',
    lazy: guard('BOOKINGS'),
    children: [
      { index: true, lazy: lazy(() => import('./pages/BookingsPage'), 'BookingsPage') },
      writes({ path: 'new', lazy: lazy(() => import('./pages/BookingsPage'), 'NewBookingPage') }),
      // K-09 (P2 #7): the full transport order — origin, consignee, load, incoterm, temperature — as a guided form.
      writes(
        { path: 'transport-order', lazy: lazy(() => import('./pages/TransportOrderWizard'), 'TransportOrderWizard') },
        { path: 'transport-order/:step', lazy: lazy(() => import('./pages/TransportOrderWizard'), 'TransportOrderWizard') },
      ),
    ],
  },
  {
    // K-09 (P5 #28): the customer announces inbound deliveries to its contract warehouses and follows them.
    path: 'asn',
    lazy: guard('ASN'),
    children: [
      { index: true, lazy: lazy(() => import('./pages/AsnPages'), 'AsnListPage') },
      writes({ path: 'new', lazy: lazy(() => import('./pages/AnnounceAsnWizard'), 'AnnounceAsnWizard') }, { path: 'new/:step', lazy: lazy(() => import('./pages/AnnounceAsnWizard'), 'AnnounceAsnWizard') }),
      { path: ':id', lazy: lazy(() => import('./pages/AsnPages'), 'AsnPage') },
    ],
  },
  {
    path: 'declarations',
    lazy: guard('CUSTOMS'),
    children: [
      { index: true, lazy: lazy(() => import('./pages/DeclarationsPages'), 'DeclarationsListPage') },
      { path: ':id', lazy: lazy(() => import('./pages/DeclarationsPages'), 'DeclarationPage') },
    ],
  },
  {
    path: 'orders',
    lazy: guard('ORDERS'),
    children: [
      { index: true, lazy: lazy(() => import('./pages/OrdersPage'), 'OrdersPage') },
      writes({ path: 'new', lazy: lazy(() => import('./pages/PlaceOrderWizard'), 'PlaceOrderWizard') }, { path: 'new/:step', lazy: lazy(() => import('./pages/PlaceOrderWizard'), 'PlaceOrderWizard') }),
      { path: ':id', lazy: lazy(() => import('./pages/DetailPages'), 'OrderPage') },
    ],
  },
  { path: 'inventory', lazy: guard('INVENTORY'), children: [{ index: true, lazy: lazy(() => import('./pages/InventoryPage'), 'InventoryPage') }] },
  {
    path: 'returns',
    lazy: guard('RETURNS'),
    children: [
      { index: true, lazy: lazy(() => import('./pages/OrdersPage'), 'ReturnsPage') },
      writes({ path: 'new', lazy: lazy(() => import('./pages/RequestReturnWizard'), 'RequestReturnWizard') }, { path: 'new/:step', lazy: lazy(() => import('./pages/RequestReturnWizard'), 'RequestReturnWizard') }),
      { path: ':id', lazy: lazy(() => import('./pages/DetailPages'), 'ReturnPage') },
    ],
  },
  { path: 'documents', lazy: guard('DOCUMENTS'), children: [{ index: true, lazy: lazy(() => import('./pages/DocumentsPage'), 'DocumentsPage') }] },
  {
    path: 'invoices',
    lazy: guard('INVOICES'),
    children: [
      { index: true, lazy: lazy(() => import('./pages/InvoicesPage'), 'InvoicesPage') },
      { path: ':id', lazy: lazy(() => import('./pages/DetailPages'), 'InvoicePage') },
    ],
  },
  {
    path: 'claims',
    lazy: guard('CLAIMS'),
    children: [
      { index: true, lazy: lazy(() => import('./pages/ClaimsPage'), 'ClaimsPage') },
      writes({ path: 'new', lazy: lazy(() => import('./pages/FileClaimWizard'), 'FileClaimWizard') }, { path: 'new/:step', lazy: lazy(() => import('./pages/FileClaimWizard'), 'FileClaimWizard') }),
      { path: ':id', lazy: lazy(() => import('./pages/DetailPages'), 'ClaimPage') },
      writes({ path: ':id/evidence/new', lazy: lazy(() => import('./pages/portalFormPages'), 'ClaimEvidencePage') }),
    ],
  },
  {
    path: 'support',
    lazy: guard('SUPPORT'),
    children: [
      { index: true, lazy: lazy(() => import('./pages/SupportPage'), 'SupportPage') },
      writes({ path: 'new', lazy: lazy(() => import('./pages/OpenCaseWizard'), 'OpenCaseWizard') }, { path: 'new/:step', lazy: lazy(() => import('./pages/OpenCaseWizard'), 'OpenCaseWizard') }),
      { path: ':id', lazy: lazy(() => import('./pages/DetailPages'), 'CasePage') },
    ],
  },
  {
    path: 'quotes',
    lazy: guard(['QUOTES', 'QUOTE']),
    children: [
      { index: true, lazy: lazy(() => import('./pages/QuotesPage'), 'QuotesPage') },
      writes({ path: 'request', lazy: lazy(() => import('./pages/RequestQuotePage'), 'RequestQuotePage') }),
      { path: ':id', lazy: lazy(() => import('./pages/DetailPages'), 'QuotePage') },
      writes({ path: ':id/accept', lazy: lazy(() => import('./pages/portalFormPages'), 'AcceptQuotePage') }, { path: ':id/decline', lazy: lazy(() => import('./pages/portalFormPages'), 'DeclineQuotePage') }),
    ],
  },
  { path: 'catalog', lazy: guard(['CATALOG', 'PRODUCTS']), children: [{ index: true, lazy: lazy(() => import('./pages/CatalogPage'), 'PortalCatalogPage') }] },
  { path: 'products', element: <Navigate to="/portal/catalog" replace /> },
  {
    // P-13: a customer administrator's own team (their party only); invite, change role and remove are pages.
    path: 'users',
    children: [
      { index: true, lazy: lazy(() => import('./pages/UsersPage'), 'UsersPage') },
      writes(
        { path: 'invite', lazy: lazy(() => import('./pages/UsersPage'), 'InviteTeamMemberPage') },
        { path: ':userId/role', lazy: lazy(() => import('./pages/UsersPage'), 'ChangeTeamMemberRolePage') },
        { path: ':userId/remove', lazy: lazy(() => import('./pages/UsersPage'), 'RemoveTeamMemberPage') },
      ),
    ],
  },
  { path: '*', element: <Navigate to="/portal" replace /> },
];

/** Mount as a sibling of the `/` shell route: `RequireAuth` around the portal's own layout, never `AppLayout`. */
export const portalRoutes: RouteObject[] = [
  {
    path: '/portal',
    // P-13 (E5): `PortalEntry` is the door — portal access, or an internal user's read-only preview of one customer.
    lazy: () =>
      import('./PortalLayout').then((m) => ({
        element: (
          <RequireAuth>
            <m.PortalEntry>
              <m.PortalLayout />
            </m.PortalEntry>
          </RequireAuth>
        ),
      })),
    errorElement: <RouteErrorBoundary />,
    children: portalChildRoutes,
  },
];

export default portalRoutes;
